import { useCallback, useMemo } from 'react';
import { useHasAnyRole, useHasRole } from '../auth';
import { AccountType } from '@/components/Modals/CreateAccount/types';
import { getMaxAnnualDiscountPercent } from '@/components/PricingPage/PricingCards/getAnnualDiscountPercent';
import { useAnnualDiscountPercent } from '@/hooks/billing/useAnnualDiscountPercent';

import type { PricingCardProps } from '@/components/PricingPage/PricingCards/PricingCard';

const accountTypeToPlanType: Partial<
  Record<AccountType, PricingCardProps['type']>
> = {
  [AccountType.Essentials]: 'Essentials',
  [AccountType.Creator]: 'Creator',
  [AccountType.Pro]: 'Pro',
  [AccountType.Business]: 'Business',
};

const FALLBACK_DISCOUNT_PERCENT = 20;

const defaultTemplateFn = (percentage: number) => `Save ${percentage}%`;

export const useAnnualDiscountPercentage = (templateFn = defaultTemplateFn) => {
  const userIsPro = useHasAnyRole(['Pro', 'Business']);
  const userIsCreator = useHasRole('Creator');
  const userIsEssentials = useHasRole('Essentials');

  const essentialsDiscount = useAnnualDiscountPercent('Essentials');
  const creatorDiscount = useAnnualDiscountPercent('Creator');
  const proDiscount = useAnnualDiscountPercent('Pro');
  const businessDiscount = useAnnualDiscountPercent('Business');

  const showNewAnnualDiscount = userIsPro;

  const annualDiscountInUpgradePill = useMemo(() => {
    if (userIsPro) {
      return templateFn(getMaxAnnualDiscountPercent());
    }
    if (userIsCreator) {
      return templateFn(creatorDiscount);
    }
    if (userIsEssentials) {
      return templateFn(essentialsDiscount);
    }

    return templateFn(FALLBACK_DISCOUNT_PERCENT);
  }, [
    userIsPro,
    userIsCreator,
    userIsEssentials,
    creatorDiscount,
    essentialsDiscount,
    templateFn,
  ]);

  const getAnnualDiscountInBillingFrequencyCard = useCallback(
    ({ accountType }: { accountType: AccountType }) => {
      const planType = accountTypeToPlanType[accountType];

      if (planType === 'Essentials') {
        return templateFn(essentialsDiscount);
      }
      if (planType === 'Creator') {
        return templateFn(creatorDiscount);
      }
      if (planType === 'Pro') {
        return templateFn(proDiscount);
      }
      if (planType === 'Business') {
        return templateFn(businessDiscount);
      }

      return templateFn(FALLBACK_DISCOUNT_PERCENT);
    },
    [
      templateFn,
      essentialsDiscount,
      creatorDiscount,
      proDiscount,
      businessDiscount,
    ]
  );

  return {
    showNewAnnualDiscount,
    annualDiscountInUpgradePill,
    getAnnualDiscountInBillingFrequencyCard,
  };
};

export const useAnnualProrateDiscount = ({
  followedByPriceInPence,
  currentYearlyTotalInPence,
}: {
  followedByPriceInPence: number;
  currentYearlyTotalInPence: number;
}) => {
  if (followedByPriceInPence === 0 || currentYearlyTotalInPence === 0) {
    return {
      savingsAsPercent: 0,
      annualDiscountText: 'Save',
    };
  }

  const savingsAsPercent = Math.max(
    0,
    // biome-ignore lint/style/noMagicNumbers: MATHS IS NOT MAGIC
    Math.floor((1 - followedByPriceInPence / currentYearlyTotalInPence) * 100)
  );
  const annualDiscountText = `Save ${savingsAsPercent}%`;

  return {
    savingsAsPercent,
    annualDiscountText,
  };
};
